import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { EmailContact, EmailContactsResponse, AdminUser } from '../types/email-viewer/email-compose.types';
import { environment } from '@sbd-doug-frontend/app/environments/environment';

/**
 * Service for managing email contacts (address book)
 */
@Injectable({
  providedIn: 'root',
})
export class EmailAddressBookService {
  private _httpClient = inject(HttpClient);

  // Cached contacts
  contacts = signal<EmailContact[]>([]);
  adminUsers = signal<AdminUser[]>([]);
  isLoading = signal<boolean>(false);

  /**
   * Fetch contacts from the API (account users)
   */
  fetchContacts(accountId: number): Observable<EmailContact[]> {
    this.isLoading.set(true);

    return this._httpClient
      .get<any>(`${environment.apiBaseUrl}/api/v4/ui-lookups/accounts/${accountId}/users`)
      .pipe(
        tap(() => this.isLoading.set(false)),
        map((response) => {
          if (response.success && response.data?.lookup?.account_users) {
            const mappedContacts = response.data.lookup.account_users.map((user: any) => {
              // Generate unique ID for tracking
              const emailKey = (user.account_user_email || '').replace(/[^a-zA-Z0-9]/g, '_');
              const id = `au${user.account_user_id}_${emailKey}`;

              return {
                id,
                account_user_id: user.account_user_id,
                email: user.account_user_email,
                contact_email_1: user.account_user_email,
                contact_type: 'account_user',
                contact_first_name: user.account_user_first_name,
                contact_last_name: user.account_user_last_name,
                display_name: `${user.account_user_first_name} ${user.account_user_last_name}`.trim(),
              } as EmailContact;
            });

            this.contacts.set(mappedContacts);
            return mappedContacts;
          }
          return [];
        })
      );
  }

  /**
   * Fetch admin users for "From" field
   */
  fetchAdminUsers(): Observable<AdminUser[]> {
    this.isLoading.set(true);

    return this._httpClient
      .get<any>(`${environment.apiBaseUrl}/api/v4/communication/email/senders`)
      .pipe(
        tap(() => this.isLoading.set(false)),
        map((response) => {
          if (response.success && response.data?.senders?.senders) {
            const mappedAdminUsers = response.data.senders.senders.map((sender: any) => {
              return {
                admin_user_id: sender.admin_user_id,
                user_first_name: sender.user_first_name,
                user_last_name: sender.user_last_name,
                user_email: sender.user_email,
                admin_email_from_name: sender.admin_email_from_name,
                admin_email_from_address: sender.admin_email_from_address,
                admin_email_signature: sender.admin_email_signature,
              } as AdminUser;
            });

            this.adminUsers.set(mappedAdminUsers);
            return mappedAdminUsers;
          }
          return [];
        })
      );
  }

  /**
   * Search contacts by query string
   */
  searchContacts(query: string): EmailContact[] {
    if (!query || query.trim().length === 0) {
      return this.contacts();
    }

    const lowerQuery = query.toLowerCase();
    return this.contacts().filter((contact) => {
      const displayName = contact.display_name?.toLowerCase() || '';
      const email = contact.email?.toLowerCase() || '';
      const company = contact.contact_company_name?.toLowerCase() || '';

      return (
        displayName.includes(lowerQuery) ||
        email.includes(lowerQuery) ||
        company.includes(lowerQuery)
      );
    });
  }

  /**
   * Create a manual email contact (typed by user)
   */
  createManualContact(email: string): EmailContact {
    return {
      id: `manual_${email.replace(/[^a-zA-Z0-9]/g, '_')}`,
      email,
      contact_email_1: email,
      contact_type: 'manual',
      display_name: email,
    };
  }

  /**
   * Validate email format
   */
  validateEmail(email: string): boolean {
    if (!email) return false;
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  /**
   * Determine contact type based on available fields
   */
  private determineContactType(
    contact: any
  ): 'account_user' | 'contact' | 'user' | 'manual' {
    if (contact.account_user_id) return 'account_user';
    if (contact.admin_user_id) return 'user';
    return 'contact';
  }

  /**
   * Get display name for contact
   */
  private getDisplayName(contact: any): string {
    const firstName = contact.contact_first_name || contact.account_user_first_name || '';
    const lastName = contact.contact_last_name || contact.account_user_last_name || '';
    const company = contact.contact_company_name || '';
    const email = contact.contact_email_1 || contact.account_user_email || '';

    if (firstName || lastName) {
      const name = `${firstName} ${lastName}`.trim();
      if (company) {
        return `${name} (${company})`;
      }
      return name || email;
    }

    if (company) {
      return company;
    }

    return email;
  }
}

